import { useParams, useNavigate } from 'react-router-dom';
import { formatDisplayDate } from '../../utils/format';
import { useTimeOff, useSubmitTimeOff, useCancelTimeOff } from '../../hooks/useTimeOff';
import { StatusBadge } from '../../components/workflow/StatusBadge';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';
import { DismissibleErrorBanner } from '../../components/common/DismissibleErrorBanner';
import { useAuditTrail } from '../../hooks/useAuditTrail';
import { StateHistory } from '../../components/workflow/StateHistory';
import { useState } from 'react';

export default function TimeOffDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const timeOffId = id ? parseInt(id, 10) : undefined;
  const { data: timeOff, isLoading, error } = useTimeOff(timeOffId);
  const { data: auditTrail } = useAuditTrail('TimeOff', timeOffId ?? 0);

  const submitMutation = useSubmitTimeOff();
  const cancelMutation = useCancelTimeOff();

  const [confirmAction, setConfirmAction] = useState<'submit' | 'delete' | null>(null);
  // F1-007: surface mutation rejections so the confirm modal closes and
  // the user sees the failure instead of an unhandled promise rejection.
  const [actionError, setActionError] = useState<string | null>(null);

  const handleAction = async () => {
    if (!timeOffId || !confirmAction) return;

    setActionError(null);
    try {
      switch (confirmAction) {
        case 'submit':
          await submitMutation.mutateAsync(timeOffId);
          break;
        case 'delete':
          await cancelMutation.mutateAsync(timeOffId);
          navigate('/time-off');
          break;
      }
    } catch (err) {
      setActionError(
        (err as Error).message ||
          (confirmAction === 'submit'
            ? 'Failed to submit time off request'
            : 'Failed to delete time off request'),
      );
    } finally {
      setConfirmAction(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error || !timeOff) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error ? (error as Error).message : 'Time off request not found'}</p>
        <Button variant="secondary" className="mt-2" onClick={() => navigate('/time-off')}>
          Back to Time Off
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/time-off')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
            &larr; Back to Time Off
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">
            {timeOff.timeOffType?.name ?? 'Time Off'}
          </h1>
        </div>
        <StatusBadge state={timeOff.state} />
      </div>

      <DismissibleErrorBanner message={actionError} onDismiss={() => setActionError(null)} />

      {/* Details Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">Type</p>
            <p className="font-medium text-gray-900">{timeOff.timeOffType?.name ?? 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Dates</p>
            <p className="font-medium text-gray-900">{formatDisplayDate(timeOff.dateFrom)} - {formatDisplayDate(timeOff.dateTo)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Duration</p>
            <p className="font-medium text-gray-900">
              {timeOff.totalDays} {timeOff.totalDays === 1 ? 'day' : 'days'} ({timeOff.totalHours}h)
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Created</p>
            <p className="font-medium text-gray-900">
              {timeOff.createdAt ? formatDisplayDate(timeOff.createdAt) : 'N/A'}
            </p>
          </div>
        </div>

        {/* Day breakdown */}
        {timeOff.days && timeOff.days.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-100">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Day Breakdown</h3>
            <div className="flex flex-wrap gap-2">
              {timeOff.days.map((day) => (
                <span
                  key={day.id}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                >
                  {formatDisplayDate(day.date)} ({day.hours}h)
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-6 pt-4 border-t border-gray-100">
          {timeOff.state === 'open' && (
            <Button onClick={() => setConfirmAction('submit')}>
              Submit for Approval
            </Button>
          )}
          {['open', 'submitted', 'approved'].includes(timeOff.state) && (
            <Button variant="danger" onClick={() => setConfirmAction('delete')}>
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Action Confirmation */}
      <ConfirmModal
        isOpen={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleAction}
        title={confirmAction === 'submit' ? 'Submit Time Off Request' : 'Delete Time Off Request'}
        message={
          confirmAction === 'submit'
            ? 'Are you sure you want to submit this time off request for approval?'
            : 'Are you sure you want to delete this time off request?'
        }
        confirmText={confirmAction === 'submit' ? 'Submit' : 'Delete'}
        variant={confirmAction === 'delete' ? 'danger' : undefined}
        isLoading={submitMutation.isPending || cancelMutation.isPending}
      />

      {/* History */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">History</h3>
        <StateHistory history={auditTrail?.data ?? []} />
      </div>
    </div>
  );
}
